'use client'

import { useEffect } from 'react'
import { EmptyState } from '@/features/shop/components/EmptyState'
import { logError } from '@/features/shared/utils/logger'

/**
 * RSC error boundary for the CDUI deals route.
 *
 * page.tsx renders delivery failures inline, so this only sees whatever else
 * throws while the segment renders. Without a boundary those reach Next's
 * unstyled default error screen.
 *
 * Scoped to this segment rather than to [store_slug] because this is the only
 * route that talks to delivery, and error.tsx does not wrap the layout in its
 * own segment — so the store layout's Header stays mounted and only the page
 * body is replaced. A boundary one level up would claim every future store
 * route and make CDUI's copy and grouping hash their default.
 */
export default function CduiDealsError({
  error,
  retry,
}: {
  error: Error & { digest?: string }
  // Next 16.3 renamed this: retry() refreshes the RSC payload and *then* clears
  // the boundary, so the delivery request actually runs again. reset() only
  // clears the boundary and re-renders the same failed payload, which can never
  // recover a delivery blip.
  retry: () => void
}) {
  useEffect(() => {
    // digest ties the client report back to the server-side stack Next kept to itself.
    logError(error, { customGroupingHash: 'CduiDeliveryStream', digest: error.digest })
  }, [error])

  return (
    <EmptyState
      text="Deals aren't loading right now"
      subText="Something went wrong on our end."
      buttonText="Try again"
      buttonAction={retry}
    />
  )
}
